import React, { useState } from "react";
import { Spinner } from "react-bootstrap";
import { toast } from "../../components/AppToast";
import GatePassList from "./GatePassList";
import GatePass from "./GatePass";
import { extractGatePassId, getGatePassById } from "../../services/gatePass/gatePass";

export default function GatePassManagement() {
  const [isCreating, setIsCreating] = useState(false);
  const [viewDoc, setViewDoc] = useState(null);
  const [editingDoc, setEditingDoc] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [listVersion, setListVersion] = useState(0);

  const closeSubView = () => {
    setIsCreating(false);
    setViewDoc(null);
    setEditingDoc(null);
    setViewLoading(false);
    setListVersion((v) => v + 1);
  };

  const activeDoc = viewDoc ?? editingDoc;

  const gatePassLabel =
    activeDoc?.gate_pass_no != null && String(activeDoc.gate_pass_no).trim()
      ? String(activeDoc.gate_pass_no).trim()
      : activeDoc?.gatePassNo != null && String(activeDoc.gatePassNo).trim()
        ? String(activeDoc.gatePassNo).trim()
        : "";

  const loadGatePassById = async (row) => {
    const id =
      extractGatePassId(row) ||
      String(row?.id || "").trim() ||
      String(row?._id || "").trim();
    if (!id) {
      toast.error(
        "Cannot open this gate pass: missing id. Check the list row payload."
      );
      return null;
    }
    setViewLoading(true);
    setViewDoc(null);
    setEditingDoc(null);
    try {
      return await getGatePassById(id);
    } catch (err) {
      const msg =
        err?.message ||
        err?.response?.data?.message ||
        "Failed to load gate pass";
      toast.error(msg);
      return null;
    } finally {
      setViewLoading(false);
    }
  };

  const headerTitle = viewDoc
    ? "View Gate Pass"
    : editingDoc
      ? "Edit Gate Pass"
      : isCreating
        ? "Create Gate Pass"
        : "Gate Pass";

  const breadcrumbLeaf = viewLoading
    ? "Loading…"
    : viewDoc
      ? gatePassLabel
        ? `View ${gatePassLabel}`
        : "View gate pass"
      : editingDoc
        ? gatePassLabel
          ? `Edit ${gatePassLabel}`
          : "Edit gate pass"
        : "Create Gate Pass";

  return (
    <div className="card shadow-lg main-card">
      <div className="card-header bg-secondary text-white d-flex justify-content-between align-items-center">
        <h4 className="mb-0 h6 fw-semibold">{headerTitle}</h4>
      </div>

      {(isCreating || viewDoc || editingDoc || viewLoading) && (
        <nav aria-label="breadcrumb" className="px-3 py-2 border-bottom bg-light">
          <ol
            className="breadcrumb mb-0 align-items-center"
            style={{ "--bs-breadcrumb-divider": "'/'" }}
          >
            <li className="breadcrumb-item">
              <button
                type="button"
                className="btn btn-link p-0 text-decoration-none text-muted"
                onClick={closeSubView}
                disabled={viewLoading}
              >
                Gate Pass
              </button>
            </li>
            <li
              className="breadcrumb-item active fw-semibold text-primary"
              aria-current="page"
            >
              {breadcrumbLeaf}
            </li>
          </ol>
        </nav>
      )}

      <div className="card-body">
        {viewLoading ? (
          <div className="d-flex align-items-center gap-2 py-5 justify-content-center text-muted">
            <Spinner animation="border" size="sm" />
            Loading gate pass…
          </div>
        ) : viewDoc ? (
          <GatePass
            key={
              extractGatePassId(viewDoc) ||
              (viewDoc?._id != null ? String(viewDoc._id) : "") ||
              "gp-view"
            }
            viewOnly
            initialDocument={viewDoc}
            onCancel={closeSubView}
            onApproved={(doc) => {
              setViewDoc(doc);
              setListVersion((v) => v + 1);
            }}
          />
        ) : editingDoc ? (
          <GatePass
            key={`${extractGatePassId(editingDoc) || editingDoc?._id || "gp"}-edit`}
            viewOnly={false}
            initialDocument={editingDoc}
            onCancel={() => {
              setEditingDoc(null);
              setListVersion((v) => v + 1);
            }}
            onSuccess={(savedDoc) => {
              setEditingDoc(null);
              setViewDoc(savedDoc);
              setListVersion((v) => v + 1);
            }}
          />
        ) : isCreating ? (
          <GatePass
            onCancel={() => {
              setIsCreating(false);
              setListVersion((v) => v + 1);
            }}
            onSuccess={(savedDoc) => {
              setIsCreating(false);
              setViewDoc(savedDoc);
              setListVersion((v) => v + 1);
            }}
          />
        ) : (
          <GatePassList
            refreshKey={listVersion}
            onCreateClick={() => setIsCreating(true)}
            onView={async (row) => {
              const doc = await loadGatePassById(row);
              if (doc) setViewDoc(doc);
            }}
            onEdit={async (row) => {
              const doc = await loadGatePassById(row);
              if (doc) setEditingDoc(doc);
            }}
          />
        )}
      </div>
    </div>
  );
}